import { AccountsContext } from '@paperstac/common/lib/components/AccountsProvider';
import { CREATE_LISTING } from '@paperstac/common/lib/serverDispatchActionTypes';
import serverDispatch from '@paperstac/common/lib/services/serverDispatch';
import { createListingValidator } from '@paperstac/firestore-collections/lib/listings';
import Box from '@paperstac/ui/lib/Box';
import Button from '@paperstac/ui/lib/Button';
import DescriptionGrid from '@paperstac/ui/lib/DescriptionGrid';
import DescriptionGridCell from '@paperstac/ui/lib/DescriptionGridCell';
import Drawer from '@paperstac/ui/lib/Drawer';
import DrawerHeader from '@paperstac/ui/lib/DrawerHeader';
import ErrorText from '@paperstac/ui/lib/ErrorText';
import Radio from '@paperstac/ui/lib/form/Radio';
import Overlay from '@paperstac/ui/lib/Overlay';
import { Form, Formik } from 'formik';
import get from 'lodash/get';
import React from 'react';
import useDimensions from '@paperstac/ui/lib/hooks/useDimensions';
import CreatePoolListingFormSection from './CreatePoolListingFormSection';
import CreateSingleListingFormSection from './CreateSingleListingFormSection';
import { MyListingsPageContext } from './MyListingsPageProvider';

const MAX_DRAWER_WIDTH = 600;

export const INITIAL_NOTE_VALUES = {
  city: '',
  country: 'US',
  lienPosition: 1,
  state: '',
  streetAddress1: '',
  streetAddress2: '',
  zip: '',
};

const CreateListingOverlay = React.memo(() => {
  const [ref, { width }] = useDimensions();
  const { currentAccountId } = React.useContext(AccountsContext);
  const { hideCreate, listings, setManageListingId } = React.useContext(MyListingsPageContext);
  const stacked = React.useMemo(() => width < MAX_DRAWER_WIDTH, [width]);
  const notesAvailableForPool = React.useMemo(() => {
    if (!listings) return [];
    return Object.values(
      listings.reduce((noteMap, listing) => {
        const [note] = listing.notes;
        if (!listing._deleted && !listing.isPool && !note.hasPoolListing) {
          noteMap[note.id] = {
            id: note.id,
            streetAddress: note.streetAddress,
            cityStateZip: note.cityStateZip,
            lienPosition: note.lienPosition,
          };
        }
        return noteMap;
      }, {})
    ).sort((a, b) => (a.streetAddress < b.streetAddress ? -1 : 1));
  }, [listings]);
  const poolAvailable = React.useMemo(() => notesAvailableForPool.length >= 2, [notesAvailableForPool]);

  return (
    <Overlay onClose={hideCreate}>
      <Drawer ref={ref} width={MAX_DRAWER_WIDTH}>
        <DrawerHeader text="Create Listing" onClose={hideCreate} px={3} />
        <Box sx={{ overflowY: 'auto', flex: '1 1 auto' }}>
          <Formik
            initialValues={{
              accountId: currentAccountId,
              isPool: false,
              notes: [INITIAL_NOTE_VALUES],
            }}
            validationSchema={createListingValidator()}
            onSubmit={(payload, { setSubmitting, setStatus }) => {
              setSubmitting(true);
              serverDispatch({ action: CREATE_LISTING, payload })
                .then(({ data: listingId }) => {
                  setManageListingId(listingId);
                  hideCreate();
                })
                .catch((error) => {
                  setStatus({ errorMessage: error.message });
                  setSubmitting(false);
                });
            }}
            children={(formik) => (
              <Form>
                <DescriptionGrid
                  sx={{
                    bg: 'gray.1',
                    border: 'none',
                    borderTop: 'default',
                    borderBottom: 'default',
                    borderRadius: 0,
                  }}
                >
                  {poolAvailable && (
                    <DescriptionGridCell term="Listing Type">
                      <Box my={3}>
                        <Box as="label" fontWeight="bold">
                          <Radio
                            onChange={() =>
                              formik.resetForm({
                                accountId: currentAccountId,
                                isPool: false,
                                notes: [INITIAL_NOTE_VALUES],
                              })
                            }
                            checked={!formik.values.isPool}
                          />{' '}
                          Single-Asset
                        </Box>
                        <Box fontSize={1}>Only a single asset for sale</Box>
                      </Box>
                      <Box>
                        <Box as="label" fontWeight="bold">
                          <Radio
                            onChange={() =>
                              formik.resetForm({
                                accountId: currentAccountId,
                                isPool: true,
                                notes: [],
                              })
                            }
                            checked={formik.values.isPool}
                          />{' '}
                          Asset Pool
                        </Box>
                        <Box fontSize={1}>Multiple assets bundled together for sale</Box>
                      </Box>
                    </DescriptionGridCell>
                  )}

                  {!formik.values.isPool && <CreateSingleListingFormSection stacked={stacked} />}

                  {formik.values.isPool && (
                    <CreatePoolListingFormSection stacked={stacked} formik={formik} notes={notesAvailableForPool} />
                  )}

                  <DescriptionGridCell sx={{ borderBottom: 'none' }}>
                    {!!get(formik, 'status.errorMessage') && <ErrorText mb={3}>{formik.status.errorMessage}</ErrorText>}
                    <Button type="submit" variant="primary" busy={formik.isSubmitting} mr={2}>
                      Create Listing
                    </Button>
                    <Button onClick={hideCreate} variant="default">
                      Cancel
                    </Button>
                  </DescriptionGridCell>
                </DescriptionGrid>
              </Form>
            )}
          />
        </Box>
      </Drawer>
    </Overlay>
  );
});

CreateListingOverlay.propTypes = {};

CreateListingOverlay.defaultProps = {};

CreateListingOverlay.displayName = 'CreateListingOverlay';

export default CreateListingOverlay;
